import { ArticleActions, ArticleGetters, ArticleMutations, ArticleState } from "@medapro-types/ContentEditor/content-editor-store";
import { createNamespacedHelpers } from "vuex-composition-helpers";

const { useState, useActions, useGetters, useMutations } = createNamespacedHelpers<ArticleState, ArticleGetters, ArticleActions, ArticleMutations>(
  "contentEditor/article"
);

export function useArticleState() {
  return useState([
    "aiTexterStyles",
    "masterdata",
    "alternativeArticleNumbers",
    "globalAttributes",
    "variantAttributes",
    "invariantAttributes",
    "multiArticlevariantAttributes",
    "erpSections",
    "mediaAssets",
    "prices",
    "priceAttributes",
    "keywords",
    "classifications",
    "references",
    "referenceTypes",
    "keywordTypes",
    "catalogs",
    "gpsr",
    "translatedMasterdata",
    "translatedGlobalAttributes",
    "translatedVariantAttributes",
    "translatedInvariantAttributes",
    "translatedPriceAttributes",
    "translatedMediaAssets",
    "translatedKeywords",
    "translatedGlobalAttributeSuggestions",
    "translatedVariantAttributeSuggestions",
    "translatedinVariantAttributeSuggestions",
    "translatedPriceAttributeSuggestions",
    "translatedMediaAssetsSuggestions",
    "translatedKeywordSuggestions",
    "globalAttributeValidationStatus",
    "variantAttributeValidationStatus",
    "invariantAttributeValidationStatus",
    "multiArticleVariantAttributeValidationStatus",
    "erpAttributeValidationStatus",
    "common",
    "metadata",
    "modified",
    "updateTasks",
    "updateErrors"
  ]);
}

export const useArticleGetters = () => {
  return useGetters(["isSaving", "hasUnsavedData", "lastRunSuccess", "languagesForEditorServices"]);
};
export const useArticleMutations = () => {
  return useMutations([
    "update-erp-attributes",
    "reset-state",
    "refresh-article-masterdata",
    "refresh-article-alternative-articlenumbers",
    "common-data-update-articlestate",
    "refresh-alternative-article-no-types",
    "refresh-reference-types",
    "refresh-keyword-types",
    "refresh-article-catalogs",
    "common-data-loaded",
    "article-error",
    "article-loaded",
    "change-article",
    "article-loading-state-update",
    "variant-attributes-loading-state-update",
    "refresh-article-global-attributes",
    "refresh-article-variant-attributes",
    "refresh-article-invariant-attributes",
    "refresh-multi-article-variant-attributes",
    "refresh-article-erp-sections",
    "refresh-article-prices",
    "refresh-article-price-attributes",
    "refresh-article-references",
    "refresh-article-keywords",
    "refresh-article-classifications",
    "refresh-article-media-assets",
    "translation-article-master-data",
    "translation-article-global-attributes",
    "translation-article-variant-attributes",
    "translation-article-invariant-attributes",
    "translation-article-price-attributes",
    "translation-article-media-assets",
    "translation-article-keywords",
    "update-save-status",
    "reset-update-status",
    "notification-seen",
    "mark-dirty-status",
    "clear-dirty-status",
    "refresh-user-list"
  ]);
};
export const useArticleActions = () => {
  return useActions([
    "loadAllCommonData",
    "loadAllStates",
    "loadAllUsers",
    "loadAlternativeArticleNoTypes",
    "loadAlternativeArticleNumbers",
    "loadArticleAttributesWithLocalizations",
    "loadArticleWithLocalizations",
    "loadAvailableAiTexterLanguages",
    "loadCatalogs",
    "loadClassifications",
    "loadErpAttributes",
    "loadGlobalAttributes",
    "loadInvariantAttributes",
    "loadKeywords",
    "loadKeywordTypes",
    "loadMasterData",
    "loadMasterDataIfNecessary",
    "loadMediaAssets",
    "loadMultiArticleVariantAttributes",
    "loadPriceAttributes",
    "loadPrices",
    "loadReferences",
    "loadReferenceTypes",
    "loadTranslatedArticleGlobalAttribute",
    "loadTranslatedArticleInvariantAttribute",
    "loadTranslatedArticleKeywords",
    "loadTranslatedArticleMasterdata",
    "loadTranslatedArticleMediaAsset",
    "loadTranslatedArticlePriceAttribute",
    "loadTranslatedArticlesWithLocalization",
    "loadTranslatedArticleVariantAttribute",
    "loadVariantAttributes",
    "loadVariantAttributesWithLocalizations",
    "loadAlternateArticleNumbers",
    "loadGpsr",
    "loadGlobalAttributeValidationStatus",
    "loadVariantAttributeValidationStatus",
    "loadMultiArticleVariantAttributeValidationStatus",
    "loadInvariantAttributeValidationStatus",
    "loadErpAttributeValidationStatus",
    "saveClassifications",
    "saveErpSections",
    "saveGlobalAttributes",
    "saveInvariantAttributes",
    "saveKeywords",
    "saveMasterData",
    "saveMediaAssets",
    "saveMultiArticleVariantAttributes",
    "savePriceAttributes",
    "savePrices",
    "saveReferences",
    "saveTranslatedMediaAssets",
    "saveTranslatedGlobalAttribute",
    "saveTranslatedInvariantAttributes",
    "saveTranslatedKeywords",
    "saveTranslatedMasterdata",
    "saveTranslatedPriceAttributes",
    "saveTranslatedVariantAttributes",
    "saveVariantAttributes",
    "saveGpsr",
    "updateErpAttributes",
    "setGpsrAssignments",
    "executeDefaultValueListSync"
  ]);
};

export function useCatalogs() {
  return useState(["catalogs"]);
}
